import React, { useState, useEffect } from "react";
import axios from "axios";
import { Menu, X } from "lucide-react";

const FacultyDashboard = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("notices");

  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    facultyName: "",
    facultyId: "",
    department: "",
    message: "",
  });

  useEffect(() => {
    fetchNotices();
  }, []);
const [queries, setQueries] = useState([]);
const [queryLoading, setQueryLoading] = useState(false);

const fetchQueries = async () => {
  setQueryLoading(true);

  try {
    const res = await axios.get(
      "https://admin.stipsredu.in/api/query/latest"
    );

    setQueries(res.data);
  } catch (err) {
    console.error("Fetch Queries Error:", err);
  } finally {
    setQueryLoading(false);
  }
};

  const fetchNotices = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log("→ Fetching notices from: https://admin.stipsredu.in/notices/latest");

      const token = localStorage.getItem("facultyToken") || localStorage.getItem("token");

      const res = await axios.get("https://admin.stipsredu.in/notices/latest", {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      console.log("← Response status:", res.status);
      console.log("← Raw response.data:", res.data);

      const data = Array.isArray(res.data)
        ? res.data
        : res.data?.notices || res.data?.data || [];

      console.log("→ Processed notices array:", data);

      setNotices(data);
    } catch (err) {
      console.error("Fetch notices failed:", err);

      let msg = "Failed to load notices";
      if (err.response) {
        msg += ` (${err.response.status})`;
        if (err.response.status === 401 || err.response.status === 403) {
          msg += " — Authentication failed. Please log in again.";
          handleLogout(); // Auto-logout on auth error
        }
      } else if (err.request) {
        msg += " - No response from server (CORS? Server down?)";
      } else {
        msg += `: ${err.message}`;
      }

      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

const handleQuerySubmit = async (e) => {
  e.preventDefault();

  // Full validation (matches backend)
  if (
    !formData.facultyName.trim() ||
    !formData.facultyId.trim() ||
    !formData.department.trim() ||
    !formData.message.trim()
  ) {
    return alert("All fields are required");
  }

  try {
    const res = await axios.post(
      "https://admin.stipsredu.in/api/query/submit",
      formData 
    );

    alert(res.data.message || "Query submitted successfully");

    // Reset form after success
    setFormData({
      facultyName: "",
      facultyId: "",
      department: "",
      message: "",
    });

  } catch (err) {
    console.error("Submit Query Error:", err);

    if (err.response) {
      alert(err.response.data.message);
    } else {
      alert("Server error. Make sure backend is running.");
    }
  }
};
  const handleLogout = () => {
    // Clear auth data
    localStorage.removeItem("facultyToken");
    localStorage.removeItem("token");
    localStorage.removeItem("user");       // if you store user info
    localStorage.removeItem("facultyData"); // adjust keys as needed

    // Redirect to login page
    window.location.href = "/faculty-login"; // or "/login" — match your route
  };

  return (
    <div className="min-h-screen flex bg-[#f8fafc]">
      {/* Mobile Hamburger */}
      <div className="md:hidden fixed top-4 left-4 z-50">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="p-2 bg-[#112E65] text-white rounded-lg"
        >
          {isOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed md:static top-0 left-0 h-full w-64 bg-[#112E65] text-white transform transition-transform duration-300 z-40
          ${isOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}
      >
        <div className="p-6 text-center border-b border-white/20">
          <h2 className="text-xl font-bold">Faculty Panel</h2>
        </div>

        <nav className="flex-1 px-4 py-6 space-y-3">
          <button
            onClick={() => { setActiveSection("notices"); setIsOpen(false); }}
            className="w-full text-left px-4 py-3 rounded-lg hover:bg-white/10"
          >
            Notices
          </button>
          <button
            onClick={() => { setActiveSection("query"); setIsOpen(false); }}
            className="w-full text-left px-4 py-3 rounded-lg hover:bg-white/10"
          >
            Submit Query
          </button>
        </nav>

        <div className="p-4 border-t border-white/20">
          <button
            onClick={handleLogout}
            className="w-full py-2 rounded-lg bg-red-500 hover:bg-red-600 transition font-medium"
          >
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-6 md:p-10 w-full">
        {activeSection === "notices" && (
          <>
            <h1 className="text-3xl font-bold text-[#112E65] mb-8">
              Notices from Principal
            </h1>

            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 text-red-700 rounded">
                {error}
                <button
                  onClick={fetchNotices}
                  className="ml-3 underline font-medium hover:text-red-800"
                >
                  Try again
                </button>
              </div>
            )}

            {loading ? (
              <div className="text-center py-12 text-gray-500">
                Loading notices...
              </div>
            ) : notices.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {notices.map((notice) => (
                  <div
                    key={notice._id || notice.id || Math.random()}
                    className="bg-white shadow-lg p-6 border-l-8 border-[#112E65] rounded-xl"
                  >
                    <h3 className="text-lg font-bold text-[#112E65] mb-2">
                      {notice.title || "Untitled Notice"}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {notice.description || notice.content || "No description"}
                    </p>
                    <span className="text-xs text-gray-400">
                      {notice.date
                        ? new Date(notice.date).toLocaleDateString()
                        : "No date"}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <p className="text-lg">No notices available at the moment.</p>
                <p className="mt-2">New notices from the Principal will appear here.</p>
              </div>
            )}
          </>
        )}

        {activeSection === "query" && (
          <>
            <h1 className="text-3xl font-bold text-[#112E65] mb-8">
              Submit Query to Principal
            </h1>

            <form
              onSubmit={handleQuerySubmit}
              className="bg-white shadow-xl p-8 rounded-xl max-w-2xl space-y-4"
            >
              <input
                type="text"
                name="facultyName"
                placeholder="Faculty Name"
                value={formData.facultyName}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#112E65]"
              />
              <input
                type="text"
                name="facultyId"
                placeholder="Faculty ID"
                value={formData.facultyId}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#112E65]"
              />
              <input
                type="text"
                name="department"
                placeholder="Department"
                value={formData.department}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#112E65]"
              />
              <textarea
                name="message"
                placeholder="Write your query here..."
                rows={4}
                value={formData.message}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#112E65]"
              />
              <button
                type="submit"
                className="px-6 py-3 bg-[#112E65] text-white rounded-lg font-semibold hover:bg-[#0d234d] transition"
              >
                Submit Query
              </button>
            </form>
            
          </>
        )}
      </main>
    </div>
    
  );
};

export default FacultyDashboard;